/**
 * shared/types/notebook.ts — Shared notebook: items, links-v1 kinds/routing, tags and suggestions, the scan, and the NotebookAPI slice.
 *
 * Moved verbatim out of the former shared/types.ts monolith; the public
 * surface is unchanged and shared/types/index.ts re-exports it.
 */
import type { GiftEnrichment, GiftEnrichStatus } from './gifts.ts';

/* ---- 4. Shared Notebook: paste link → card, unfurl, agent categorize ---- */

/** Epic szvNTWusHt3f (Jonathan's feature addition, not in PRD §6/§7).
 *  'trailer' stopped being a category (card 3ThoVHSXNpjo): a trailer is filed
 *  under the thing it trails — TV or a movie. Nothing may write it back: the
 *  API validates against NOTEBOOK_CATEGORIES, the model prompt is built from
 *  it, classify() never returns it, and the durable object blocks it at the
 *  schema and trigger level. */
export type NotebookCategory =
  | 'movie'
  | 'tv'
  | 'music'
  | 'podcast'
  | 'video'
  | 'article'
  | 'other';

export const NOTEBOOK_CATEGORIES = [
  'movie', 'tv', 'music', 'podcast', 'video', 'article', 'other',
] as const;

/** Notebook v2 typed collections (design v1.1 §5.3 / §7): a saved thing is a
 *  link (paste) or a plain-text quick add. entry_type names the collection it
 *  belongs to; category stays the classifier's media verdict for links. */
export type NotebookEntryType = 'link' | 'gift' | 'memory' | 'date' | 'note' | 'idea';
export const NOTEBOOK_ENTRY_TYPES = ['link', 'gift', 'memory', 'date', 'note', 'idea'] as const;

/** Every card links out by intent: movie→play, album→stream, podcast→play, article→read. */
export type NotebookIntent = 'play' | 'stream' | 'read' | 'open';

/* ── Links v1 data model (card upbzFPmJUl7Q, PRD §5/§7) ───────────────────────
 * "Types are data, destinations are views": one link object carries a kind and
 * a subtype, and Dates / Gift ideas / Notebook / Do together are views over it,
 * never copies. The kind is the closed top-level set (the chips); the subtype
 * is open-ended and falls back to the kind when unknown. Facets (status, scope,
 * intent, place, when, saved-by, tags) apply to every kind.
 *
 * Validation lives here, not in SQL CHECK constraints: a constraint an older
 * table cannot satisfy takes the whole Durable Object room down at boot (see the
 * hazard note at the top of NOTEBOOK_SCHEMA_SQL), and the new columns are added
 * to tables that predate them. */
export type NotebookLinkKind =
  | 'watch'
  | 'listen'
  | 'read'
  | 'play'
  | 'learn'
  | 'mementos'
  | 'places'
  | 'events'
  | 'trips'
  | 'things'
  | 'make'
  | 'admin'
  | 'other';

/** Chip order is the founder's (2026-10-17): Watch · Listen · Read · Play ·
 *  Learn · Mementos, then the rest. NOTEBOOK_LINK_KINDS is also the display
 *  order, so this array is load-bearing. */
export const NOTEBOOK_LINK_KINDS = [
  'watch', 'listen', 'read', 'play', 'learn', 'mementos',
  'places', 'events', 'trips', 'things', 'make', 'admin', 'other',
] as const;

/** Kind → the subtypes the PRD names as examples (PRD §5 layer 1). Open-ended:
 *  a subtype not in this list is still stored, and the UI falls back to the kind.
 *  Founder's call 2026-10-17: Learn owns courses and tutorials, so Make no
 *  longer lists tutorials (Make is recipes and DIY). */
export const NOTEBOOK_LINK_SUBTYPES: Record<NotebookLinkKind, readonly string[]> = {
  watch: ['movies', 'tv', 'documentaries', 'video'],
  listen: ['podcasts', 'albums', 'playlists'],
  read: ['books', 'articles', 'newsletters'],
  play: ['video-games', 'board-games'],
  learn: ['courses', 'tutorials'],
  mementos: ['photo-albums', 'keepsakes'],
  places: ['restaurants', 'cafes', 'bars', 'parks', 'venues', 'attractions'],
  events: ['gigs', 'theatre', 'comedy', 'exhibitions', 'festivals', 'markets', 'screenings'],
  trips: ['destinations', 'stays', 'itineraries'],
  things: ['gifts', 'home', 'clothes', 'tech'],
  make: ['recipes', 'diy'],
  admin: ['bookings', 'tickets', 'property', 'forms'],
  other: [],
};

export const isNotebookLinkKind = (v: unknown): v is NotebookLinkKind =>
  typeof v === 'string' && (NOTEBOOK_LINK_KINDS as readonly string[]).includes(v);

/** Facet: lifecycle (PRD §6.6). Events and trips carry a date and auto-move to
 *  done once past; planned is set when a date or plan is affirmed by both. */
export type NotebookLinkStatus = 'saved' | 'planned' | 'done' | 'passed';
export const NOTEBOOK_LINK_STATUSES = ['saved', 'planned', 'done', 'passed'] as const;

export const isNotebookLinkStatus = (v: unknown): v is NotebookLinkStatus =>
  typeof v === 'string' && (NOTEBOOK_LINK_STATUSES as readonly string[]).includes(v);

/** Facet: who can see it (PRD §6.4). Scope is decided by the routing table,
 *  never by the classifier's confidence alone — gifts and products default to
 *  just-me, and me→shared is always a deliberate action. */
export type NotebookLinkScope = 'shared' | 'just_me';
export const NOTEBOOK_LINK_SCOPES = ['shared', 'just_me'] as const;

/** How the link got in (PRD §6.1 entry points). */
export type NotebookLinkSource = 'app' | 'share' | 'email';
export const NOTEBOOK_LINK_SOURCES = ['app', 'share', 'email'] as const;

export const isNotebookLinkSource = (v: unknown): v is NotebookLinkSource =>
  typeof v === 'string' && (NOTEBOOK_LINK_SOURCES as readonly string[]).includes(v);

/* ── Routing table (card 4CXp7HfSIAGd, PRD §6.3/§6.4) ────────────────────────
 * Every kind has ONE home — the surface its cards appear on — plus the default
 * scope and the next action its card offers. Destinations are views over one
 * object ("types are data"), so this table never copies a row: it answers where
 * the same row shows up and what you can do with it next.
 *
 * Scope is decided HERE, never by the classifier's confidence (PRD §6.4): a
 * just-me default is the one decision that cannot be undone quietly, so a kind
 * that defaults private (Things — gifts and products) is tightened to just-me
 * by the writer, and only a deliberate human action may widen it back.
 *
 * Home is a view id, not a route: the Notebook screen renders every kind as a
 * chip, and the destinations (Dates, Gift ideas, Do together, Requests) read
 * the same rows. Adding a destination means adding an id here and a view that
 * consumes it — never a second copy of the link. */
export type NotebookLinkHome =
  /** The links index itself — saved cards, kind chips, search. */
  | 'notebook'
  /** Gift ideas — private by default so a surprise stays a surprise. */
  | 'gift_ideas'
  /** Dates › Places (map pin). */
  | 'dates_places'
  /** Dates › Ideas. */
  | 'dates_ideas'
  /** Do together › Plans. */
  | 'plans'
  /** Do together › Cook / Projects. */
  | 'make'
  /** Requests (life admin) › follow-ups. */
  | 'requests';

/** What the card's primary button offers (PRD §6.3 "next action"). */
export type NotebookNextAction =
  | 'nominate_date'
  | 'watch_together'
  | 'listen_together'
  | 'read_together'
  | 'play_together'
  | 'learn_together'
  | 'revisit'
  | 'add_to_wishlist'
  | 'start_plan'
  | 'add_to_plan'
  | 'cook_it'
  | 'create_task'
  | 'reclassify';

export interface NotebookLinkRoute {
  /** The kind's own name, for the chip row (the founder's taxonomy: Watch ·
   *  Listen · Read · Play · Learn · Mementos, then the rest). The home label is
   *  where it files, which is not always the same word — Watch and Listen both
   *  file under Notebook › Media, and a row of two "Media" chips tells you
   *  nothing. */
  kindLabel: string;
  home: NotebookLinkHome;
  /** The destination as a person reads it — receipt copy and group headings. */
  homeLabel: string;
  defaultScope: NotebookLinkScope;
  nextAction: NotebookNextAction;
  /** The verb on the card's primary button. */
  nextActionLabel: string;
}

/** Kind → route. Every kind in NOTEBOOK_LINK_KINDS MUST appear here: the probe
 *  asserts the key set against the chip order, so a new kind cannot ship with
 *  no home. */
export const NOTEBOOK_LINK_ROUTING: Record<NotebookLinkKind, NotebookLinkRoute> = {
  watch: {
    kindLabel: 'Watch',
    home: 'notebook', homeLabel: 'Notebook › Media', defaultScope: 'shared',
    nextAction: 'watch_together', nextActionLabel: 'Watch together',
  },
  listen: {
    kindLabel: 'Listen',
    home: 'notebook', homeLabel: 'Notebook › Media', defaultScope: 'shared',
    nextAction: 'listen_together', nextActionLabel: 'Listen together',
  },
  read: {
    kindLabel: 'Read',
    home: 'notebook', homeLabel: 'Notebook › Reading', defaultScope: 'shared',
    nextAction: 'read_together', nextActionLabel: 'Read together',
  },
  play: {
    kindLabel: 'Play',
    home: 'notebook', homeLabel: 'Notebook › Games', defaultScope: 'shared',
    nextAction: 'play_together', nextActionLabel: 'Play together',
  },
  learn: {
    kindLabel: 'Learn',
    home: 'notebook', homeLabel: 'Notebook › Learning', defaultScope: 'shared',
    nextAction: 'learn_together', nextActionLabel: 'Learn together',
  },
  mementos: {
    kindLabel: 'Mementos',
    home: 'notebook', homeLabel: 'Notebook › Mementos', defaultScope: 'shared',
    nextAction: 'revisit', nextActionLabel: 'Revisit it',
  },
  places: {
    kindLabel: 'Places',
    home: 'dates_places', homeLabel: 'Dates › Places', defaultScope: 'shared',
    nextAction: 'nominate_date', nextActionLabel: 'Nominate for a date',
  },
  events: {
    kindLabel: 'Events',
    home: 'dates_ideas', homeLabel: 'Dates › Ideas', defaultScope: 'shared',
    nextAction: 'nominate_date', nextActionLabel: 'Nominate for a date',
  },
  trips: {
    kindLabel: 'Trips',
    home: 'plans', homeLabel: 'Do together › Plans', defaultScope: 'shared',
    nextAction: 'start_plan', nextActionLabel: 'Start a plan',
  },
  things: {
    kindLabel: 'Gifts',
    home: 'gift_ideas', homeLabel: 'Gift ideas', defaultScope: 'just_me',
    nextAction: 'add_to_wishlist', nextActionLabel: 'Add to wishlist',
  },
  make: {
    kindLabel: 'Make',
    home: 'make', homeLabel: 'Do together › Cook & Projects', defaultScope: 'shared',
    nextAction: 'cook_it', nextActionLabel: 'Add to a plan',
  },
  admin: {
    kindLabel: 'Admin',
    home: 'requests', homeLabel: 'Requests', defaultScope: 'shared',
    nextAction: 'create_task', nextActionLabel: 'Create a follow-up',
  },
  other: {
    kindLabel: 'Other',
    home: 'notebook', homeLabel: 'Notebook › Saved', defaultScope: 'shared',
    nextAction: 'reclassify', nextActionLabel: 'Reclassify',
  },
};

/** Unknown kind (a row written before the table, or a future kind) falls back
 *  to the catch-all route rather than throwing: a read must never fail because
 *  a card is unusual. */
export const notebookRouteFor = (kind: string): NotebookLinkRoute =>
  (NOTEBOOK_LINK_ROUTING as Record<string, NotebookLinkRoute>)[kind] ?? NOTEBOOK_LINK_ROUTING.other;

/** Does filing this kind tighten the row to private? True only for a kind whose
 *  route defaults to just-me — the writer applies it in ONE direction, so a
 *  shared row never becomes private by accident and a private row is never
 *  published by a classifier. */
export const notebookRouteTightensScope = (kind: string): boolean =>
  notebookRouteFor(kind).defaultScope === 'just_me';

/** One row per real-world thing a link points at (PRD §7). Multiple links to
 *  the same entity merge, and "both saved" is an entity both partners linked. */
export interface NotebookEntity {
  id: string;
  kind: NotebookLinkKind;
  name: string;
  canonicalUrl: string | null;
  place: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: number;
}


export interface NotebookItem {
  itemId: string;
  url: string;
  /** Unfurled title; null until the metadata fetch resolves (never blocks the paste). */
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  durationSeconds: number | null;
  /** youtube | spotify | apple-podcasts | letterboxd | imdb | article | unknown */
  provider: string;
  category: NotebookCategory;
  /** Why it was categorized this way — contract: non-empty whenever category != 'other'. */
  categoryReason: string;
  /** null = partner has not classified it either (manual tagging path). */
  categorizedBy: string | null;
  intent: NotebookIntent;
  /** Shared with partner by default (PRD §4 principle 3). */
  shared: boolean;
  /** Set by either partner; reflects to the other on reconnect. */
  doneAt: number | null;
  addedBy: string;
  createdAt: number;
  updatedAt: number;
  /** Notebook v2 collection this entry belongs to; 'link' for any paste. */
  entryType: NotebookEntryType;
  /** Plain-text body for gift/memory/date/note/idea quick adds; null for links. */
  body: string | null;
  /** Gift-suggestion enrichment state (card OviOTaNsNivQ). '' = not a gift
   *  (or a row saved before the feature); 'pending' = the search is running;
   *  'done' = detail landed; 'off' = concierge toggle off, nothing ran;
   *  'none' = the search ran and came back empty. */
  enrichStatus: GiftEnrichStatus;
  /** Parsed `enrichment` column — null unless enrichStatus === 'done'. */
  enrichment: GiftEnrichment | null;
  /** The plan session that spawned this entry (card 7aTCRQ9j8PWV, design §6);
   *  null on anything not filed from a weekly meeting. */
  sourceEventId: string | null;
  /* ── Links v1 (card upbzFPmJUl7Q, PRD §5/§7). Written alongside the legacy
   *  category/entry_type/shared columns, which stay authoritative until the
   *  reader cards switch over. */
  /** Top-level kind — the chip the link files under. */
  kind: NotebookLinkKind;
  /** Free-form subtype under the kind; null until the classifier or the user
   *  picks one (the UI falls back to the kind). */
  subtype: string | null;
  /** The real-world thing this link points at; null until entity resolution
   *  lands (PRD §6.5, "Next" phase). */
  entityId: string | null;
  status: NotebookLinkStatus;
  scope: NotebookLinkScope;
  /** City or "near us" (PRD §5 layer 3). */
  place: string | null;
  /** Event date or expiry, epoch ms; events and trips only. */
  eventDate: number | null;
  source: NotebookLinkSource | null;
  /** Classifier confidence 0..1; null on rows saved before the model existed. */
  confidence: number | null;
  /** Why the cascade filed this kind (card 5ZQIfujFJMjE) — the receipt shows
   *  it verbatim, and it is separate from the media-category reason. */
  kindReason: string | null;
  /** The band the confidence produced ('auto' | 'check' | 'ask'); null on a
   *  row the cascade never saw. An 'ask' row is not a confident filing. */
  kindTier: string | null;
  /** For 'ask': the 2-4 ranked candidates the chooser offers, best guess
   *  first. null when the cascade had nothing to offer. */
  kindOptions: Array<{ kind: NotebookLinkKind; subtype: string | null; confidence: number | null }> | null;
}

/**
 * Tags a partner (or the agent, once accepted) hangs off a saved entry —
 * card ioKcDXGeHnE7: mood / theme / time, suggested by the scan, applied
 * only by a human. The category column stays what the entry IS; these are
 * what it feels like / is about / when it belongs to.
 */
export type NotebookTagKind = 'mood' | 'theme' | 'time';
export const NOTEBOOK_TAG_KINDS = ['mood', 'theme', 'time'] as const;

/**
 * Closed theme vocabulary (card 5fEg3YWwWZhF — anti-category-creep).
 *
 * A theme is what an entry is ABOUT at a human level. The specific subject
 * (aviation, football, true crime) is never a theme: it is a detail, and it
 * is only kept when it matches an interest already in a partner's profile.
 * The model picks from this list and nothing else — anything outside it is
 * dropped before storage, so the filter list stays readable forever.
 *
 * Widening the set is a deliberate human edit HERE, never a model decision.
 */
export const NOTEBOOK_THEME_VOCAB = [
  'news',
  'thought-provoking',
  'how-to',
  'story',
  'comedy',
  'chill',
  'big-feeling',
  'learn-something',
  'plan-something',
] as const;

export type NotebookTheme = (typeof NOTEBOOK_THEME_VOCAB)[number];

export function isNotebookTheme(tag: string): tag is NotebookTheme {
  return (NOTEBOOK_THEME_VOCAB as readonly string[]).includes(tag);
}

/**
 * Editorial wording for the theme keys (card CTVSTgXykQ0Y, carried by
 * BS0NIMpbbuC5) — ONE vocabulary for the whole app: the notebook card, the
 * digest's source list and any API consumer read THIS map. The stored tag,
 * the filter key, the API payload and every data-* attribute keep the RAW
 * key (NOTEBOOK_THEME_VOCAB), so search and filters never depend on prose.
 * A key missing from the map (a word someone typed themselves) falls back to
 * the raw key — never blank.
 */
export const NOTEBOOK_THEME_LABELS: Record<string, string> = {
  news: 'In the news',
  'thought-provoking': 'Worth a thought',
  'how-to': 'How to do it',
  story: 'A good story',
  comedy: 'Something funny',
  chill: 'Something easy',
  'big-feeling': 'Felt this one',
  'learn-something': 'Learn something',
  'plan-something': 'Something to plan',
};

/** Display-only render of a theme key; mood/time tags print as typed. */
export function notebookThemeLabel(tag: string): string {
  return NOTEBOOK_THEME_LABELS[tag] ?? tag;
}

export interface NotebookTag {
  id: string;
  itemId: string;
  kind: NotebookTagKind;
  tag: string;
  /** 'human' typed by a partner, 'agent' applied from an accepted suggestion. */
  source: 'human' | 'agent';
  addedBy: string | null;
  createdAt: number;
}

export type NotebookSuggestionStatus = 'pending' | 'accepted' | 'overridden' | 'dismissed';
export const NOTEBOOK_SUGGESTION_STATUSES = ['pending', 'accepted', 'overridden', 'dismissed'] as const;

export interface NotebookTagSuggestion {
  id: string;
  itemId: string;
  kind: NotebookTagKind;
  tag: string;
  /** Why the agent proposed it — required on every row, rendered verbatim. */
  rationale: string;
  /**
   * The concrete subject the model saw, kept ONLY when it matches an interest
   * in a partner's profile (card 0OvyJbpnACMX); null otherwise. Never a tag —
   * the closed theme vocabulary owns tags, this is the "about aviation" note.
   */
  subject: string | null;
  status: NotebookSuggestionStatus;
  scanAt: number;
  resolvedAt: number | null;
  resolvedBy: string | null;
  createdAt: number;
}

/** One proposed tag coming back from the model; validated before it is stored. */
export interface NotebookTagProposal {
  itemId: string;
  kind: NotebookTagKind;
  tag: string;
  rationale: string;
  /** The concrete subject the model saw (aviation, football). Never stored as
   *  a theme — only kept when it matches an interest in a partner's profile
   *  (card 0OvyJbpnACMX), and dropped otherwise. */
  subject?: string;
}

export interface NotebookAPI {
  /** Paste a URL → card created immediately (raw URL), unfurl runs async. */
  /** `gift` is the gift toggle (card PQX46vNPdbwn): it files the paste under
   *  Things › Gifts, which the routing table defaults to just-me; `shared: true`
   *  is the user deliberately widening that link to the couple. */
  addNotebookItem(input: { url: string; addedBy: string; gift?: boolean; shared?: boolean }): Promise<NotebookItem>;
  /** Notebook v2 quick add (design v1.1 §5.3): a typed plain-text entry
   *  (gift/memory/date/note/idea). url is empty — the body IS the entry. */
  addQuickEntry(input: {
    type: NotebookEntryType;
    body: string;
    addedBy: string;
    /** Visibility for typed entries (card sYwPmYEIf4VC): 'us' (default) is
     *  shared with the partner, 'me' is private to `addedBy`. Links are
     *  always shared — they are what the notebook is for. */
    shared?: boolean;
    /** Sub-type marker for a typed entry (meeting transcription card
     *  a0ABwZvTf9ct sets 'meeting'). Plain text stays 'plain-text'. This is
     *  the column `provider` already had — using it instead of a new
     *  entry_type is deliberate: a new entry_type needs a CHECK-constraint
     *  migration on every couple's notebook table. */
    provider?: string;
    /** Audio length in seconds for a transcription card (NULL for anything
     *  hand-typed). Stored in the existing duration_seconds column. */
    durationSeconds?: number | null;
    /** Gift idea only (card OviOTaNsNivQ): 'pending' when the concierge is
     *  on and a search will run after the response, 'off' when it will not.
     *  Anything else leaves the row at '' (not a gift / not attempted). */
    enrichStatus?: string;
    /** Server-side idempotency key for the event this entry files (card
     *  eq92fFXqQUuP, sol review 2026-10-19): the caller can read it back to
     *  ask "did this event already file?" before inserting, so a crash or a
     *  concurrent retry cannot duplicate the entry. Omitted by every
     *  pre-existing caller. */
    sourceEventId?: string;
  }): Promise<NotebookItem>;
  /** Write the gift-enrichment result back onto a gift card — the status
   *  ('done' | 'none' | …) plus the detail JSON, or null for 'none'. Gift
   *  rows only; anything else answers `not_a_gift`. */
  setGiftEnrichment(itemId: string, status: string, enrichment: GiftEnrichment | null): Promise<NotebookItem>;
  /** Remove a user-authored entry outright (gift/memory/date/note/idea). */
  /** `viewerId` is enforced: a member cannot act on a card they cannot see
   *  (a private card answers not_found, so its existence never leaks). */
  removeNotebookEntry(itemId: string, viewerId: string): Promise<NotebookItem>;

  /** Card 67zOnRgx4YTI item 1 — the saver rewrites their own card's words
   *  (link: title/description, typed entry: body). Same gate as remove. */
  editNotebookEntry(
    itemId: string,
    viewerId: string,
    patch: {
      title?: string;
      description?: string;
      body?: string;
      /* Card 2qBAZOM2LFWa — a card's own detail (the day, the place). Both
         are nullable: `null` clears, `undefined` leaves the row alone. */
      eventDate?: number | null;
      place?: string | null;
    },
  ): Promise<NotebookItem>;
  /** The list as `viewerId` may see it: every shared card plus their own
   *  private ones. Private cards are filtered here (not at the edge) so no
   *  future caller can forget. `q` is the collection search: when non-empty,
   *  only items whose text fields OR tags match it (case-insensitive
   *  substring) come back — the match is a query, not a client-side filter,
   *  because the list is paginated (card UVRx4xesTEig). */
  listNotebook(viewerId: string, q?: string): Promise<NotebookItem[]>;
  /** Called by the worker once unfurl returns; merge metadata, keep raw URL on failure.
   *  `intent` is OPTIONAL and defaults to "leave the column alone" (card
   *  QqIIMN87A0RB): the verb on a card is a classification, and a caller that
   *  only has metadata to add — settleFiledLink — must not be able to restyle
   *  it. The unfurl doors that DO classify pass it explicitly. */
  setNotebookMeta(itemId: string, meta: {
    title: string | null;
    description: string | null;
    imageUrl: string | null;
    durationSeconds: number | null;
    provider: string;
    intent?: NotebookIntent;
  }): Promise<NotebookItem>;
  /** Agent or manual classification. Empty reason throws Error('reason_required'). */
  categorizeNotebook(itemId: string, category: NotebookCategory, reason: string, byMemberId: string | null): Promise<NotebookItem>;
  /** Links v1 cascade filing (card 5ZQIfujFJMjE, PRD §6.2): writes kind,
   *  subtype, the classifier's confidence, and the band + ranked options.
   *  Links only — a quick-add answers not_a_link. A member's own retag
   *  (categorized_by set) answers overridden_by_member: a human decision
   *  always beats the background filing. Empty reason on a filed kind
   *  throws Error('reason_required'), same contract as categorizeNotebook. */
  setLinkFiling(itemId: string, filing: {
    kind: NotebookLinkKind;
    subtype: string | null;
    confidence: number;
    reason: string;
    tier: string;
    options: Array<{ kind: NotebookLinkKind; subtype: string | null; confidence: number }>;
  }): Promise<NotebookItem>;
  /** The chooser's write (card xNGOA5rZz9LV, sol review 2026-10-19): a person
   *  answering an 'ask' row files the kind themselves — any member who can see
   *  the card, same contract as categorizeNotebook (empty reason on a filed kind
   *  throws Error('reason_required')). Links only — a quick-add answers
   *  not_a_link. Success clears kind_tier/kind_options, so the chooser leaves
   *  with the question it answered, and sets categorized_by, which makes the
   *  decision terminal for the background cascade. */
  fileNotebookKind(itemId: string, viewerId: string, kind: NotebookLinkKind, subtype: string | null, reason: string): Promise<NotebookItem>;
  /** watched/played/done — toggled by either partner. */
  markNotebookDone(itemId: string, done: boolean, viewerId: string): Promise<NotebookItem>;
  /** Items whose metadata changed since the scan cursor — the scan's working set. */
  listNotebookSince(since: number, limit?: number): Promise<NotebookItem[]>;
  /** Tags already on an entry; omit itemId for every tag in the notebook.
   *  Scoped: `viewerId` gates the per-item read and joins the notebook-wide
   *  list through the visibility seam (card fyOZFYwAtbZy), so tags derived
   *  from a partner's just-me row never leave the room. */
  listNotebookTags(viewerId: string, itemId?: string): Promise<NotebookTag[]>;
  /** Deliberate visibility change, OWNER only (card fyOZFYwAtbZy, PRD §8):
   *  me→us publishes to the partner, us→me takes it back. A partner's
   *  private row answers not_found (existence never leaks); a shared row
   *  that isn't theirs answers forbidden. */
  setNotebookVisibility(itemId: string, viewerId: string, shared: boolean): Promise<NotebookItem>;
  /** Suggestions the viewer may see (scoped through the notebook row, card
   *  fyOZFYwAtbZy); omit status for pending only. */
  listTagSuggestions(viewerId: string, status?: NotebookSuggestionStatus): Promise<NotebookTagSuggestion[]>;
  /** Persist one scan's proposals; a duplicate (item, kind, tag) is skipped. */
  recordTagSuggestions(input: { proposals: NotebookTagProposal[]; scanAt: number }): Promise<NotebookTagSuggestion[]>;
  /** Human accept (apply the agent's tag) or override (apply their own word). */
  resolveTagSuggestion(input: {
    id: string;
    action: 'accept' | 'override';
    tag?: string;
    memberId: string;
  }): Promise<NotebookTagSuggestion>;
  /** Last successful scan timestamp (0 = never), for the daily cursor. */
  scanCursor(): Promise<number>;
  setScanCursor(ts: number): Promise<void>;
}

/**
 * Outcome of one notebook tag scan (card ioKcDXGeHnE7). `scanned` is true
 * only when the model actually ran; every other stop names why it did not,
 * so a probe can assert "toggle off = no scan" without reading the tables.
 */
export type NotebookScanStop =
  | 'not_plus'
  | 'toggle_off'
  | 'no_model_key'
  | 'no_new_items'
  | 'model_error'
  | 'emitted'
  | 'no_signal';

export interface NotebookScanResult {
  scanned: boolean;
  reason: NotebookScanStop;
  /** Suggestions persisted by this run. */
  count: number;
  /** Entries the run considered. */
  considered: number;
}

/** Lives on the DO itself (it needs env + the message fan-out), not on the slice. */
export interface NotebookScanAPI {
  runNotebookScan(): Promise<NotebookScanResult>;
}
